import React, { useState, useEffect } from "react";
import SEO from "../shared/SEO";
import { useNavigate } from "react-router-dom";
import { signInWithPopup, OAuthProvider } from "firebase/auth";
import { auth } from "../../firebaseConfig";
import { jwtDecode } from "jwt-decode";
import ContentImage from "../../assets/authContentImage.png";
import MicrosoftIcon from "../../assets/ms-login.svg";
import { GoogleOAuthProvider, GoogleLogin,useGoogleLogin } from "@react-oauth/google";
import { IpInfoResponse } from "../../types/authTypes";
import { useDispatch } from "react-redux";
import { setUserPlan } from "../../redux/slices/userSlice";
import { Theme } from "../../lib/theme";
import { toast } from "react-toastify";

import orcaAuth from "../../assets/orcaAuth.png";
import Navbar from "../orcus-landing/Navbar";
import { FaArrowRight, FaCheck } from "react-icons/fa";
import Footer from "../orcus-landing/Footer";
import ImageSlider, { Slide } from "./imageSlider";
import image1 from "../../assets/loginImage1.png";
import image2 from "../../assets/loginImage2.png";
import image3 from "../../assets/loginImage3.png";
import image4 from "../../assets/loginImage4.png";
import image5 from "../../assets/loginImage5.png"
import image from "../../assets/image.png";
import herosection from "../../assets/frame4.png";
import ebook1 from "../../assets/ebook1.jpg";
import ebook2 from "../../assets/ebook2.jpg";

export interface DecodedToken {
  email: string;
  name: string;
  picture?: string;
  exp?: number;
}
const features: string[] = [
  "The Orca Blueprint: The art of building and scaling your startup.",
  "AI Pitch Deck Generator",
  "Professional Pitch Evaluation",
  "Investor Scouting Portal",
  "Annual Funding Opportunity",
];

const slides: Slide[] = [
  {
    imageUrl: image1,
    dotPosition: { top: "20%", left: "30%" },
    show: false,
  },
  {
    imageUrl: image2,
    dotPosition: { top: "50%", left: "60%" },
    show: false,
  },
  {
    imageUrl: image3,
    dotPosition: { top: "50%", left: "60%" },
    show: false,
  },
   {
    imageUrl: image4,
    dotPosition: { top: "50%", left: "60%" },
    show: false,
  },
  {
    imageUrl: image5,
    dotPosition: { top: "50%", left: "60%" },
    show: false,
  },
];
function Login() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const defaultAvatarUrl =
    "https://github.com/parati-eos/EOS_DEPLOYMENT/blob/main/download__11_-removebg-preview%20(1).png?raw=true";

  useEffect(() => {
    const checkTokenExpiry = (token: string | null) => {
      if (!token) return;

      try {
        const decoded: DecodedToken = jwtDecode(token);
        const currentTime = Math.floor(Date.now() / 1000);

        if (decoded.exp && decoded.exp > currentTime) {
          const timeoutDuration = (decoded.exp - currentTime) * 1000;
          setTimeout(() => {
            handleLogout();
          }, timeoutDuration);
        } else {
          handleLogout();
        }
      } catch (error) {
        console.error("Invalid token:", error);
        handleLogout();
      }
    };

    const handleLogout = () => {
      localStorage.removeItem("token");
      window.location.reload();
    };

    const token = localStorage.getItem("token");
    checkTokenExpiry(token);
  }, []);

  const generateOrgId = () => "Parati-" + Date.now();
  const generatedOrgId = generateOrgId();

  const blockIfFromIsrael = async (): Promise<boolean> => {
    try {
      const res = await fetch("https://api.zynth.ai/api/v1/data/userprofile/ip-info");
      const ipInfo: IpInfoResponse = await res.json();
      if (ipInfo.country?.toLowerCase() === "il") {
        toast.error("Access not allowed from Israel", {
          position: "top-center",
          autoClose: 4000,
        });
        return true;
      }
    } catch (err) {
      console.error("Failed to check IP info:", err);
    }
    return false;
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    const blocked = await blockIfFromIsrael();
    if (blocked) return;

    const decoded: DecodedToken = jwtDecode(credentialResponse.credential);
    sessionStorage.setItem("userEmail", decoded.email);
    sessionStorage.setItem("userDP", decoded.picture || defaultAvatarUrl);
    sessionStorage.setItem("referrerName", decoded.name);

    const userData = {
      name: decoded.name,
      email: decoded.email,
    };

    saveUserData(userData);
  };

  const handleMicrosoftLogin = async () => {
    const blocked = await blockIfFromIsrael();
    if (blocked) return;

    const provider = new OAuthProvider("microsoft.com");
    try {
      const result = await signInWithPopup(auth, provider);
      if (result && result.user) {
        const userData = {
          name: result.user.displayName || "",
          email: result.user.email || "",
          picture: result.user.photoURL || defaultAvatarUrl,
        };

        sessionStorage.setItem("userEmail", userData.email);
        sessionStorage.setItem("userDP", userData.picture);

        saveUserData(userData);
      } else {
        throw new Error("Microsoft Login Failed: No access token found");
      }
    } catch (error) {
      console.error("Microsoft Login Failed:", error);
    }
  };

  const saveUserData = async (userData: Record<string, any>) => {
    setIsLoading(true);
    const signupLink = localStorage.getItem("sign_up_link") || "";
    try {
      const ipInfoResponse = await fetch("https://api.zynth.ai/api/v1/data/userprofile/ip-info");
      const ipInfoData: IpInfoResponse = await ipInfoResponse.json();

      const userPayload = {
        userId: userData.email,
        name: userData.name,
        orgId: generatedOrgId,
        pptCount: 0,
        userIPCountry: ipInfoData.country!,
        signupLink,
        source:"orcas",
        currentSource:"orcas"
      };

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/data/userprofile/user`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(userPayload),
        }
      );

      const responseData = await res.json();
      if (responseData.orgid && responseData.plan?.plan_name) {
        dispatch(setUserPlan(responseData.plan.plan_name));
        sessionStorage.setItem("orgId", responseData.orgid);
        navigate("/new-presentation");
      } else {
        sessionStorage.setItem("orgId", generatedOrgId);
        navigate("/onboarding");
      }
      sessionStorage.setItem("authToken", responseData.token);
    } catch (error) {
      console.error("Error storing user data:", error);
    } finally {
      setIsLoading(false);
    }
  };
  

  return (
    <div>
      <SEO
        title="Login | Access Orcas Valley Startup Platform"
        description="Access The Orca Blueprint, AI pitch deck generator, investor portal, and funding opportunities in one platform."
        url="https://orcasvalley.com/auth"
        keywords="startup platform login, pitch deck generator login, startup accelerator access, entrepreneur portal login"
      />
      <Navbar />
      <header className="relative text-white h-[110vh] w-full md:h-[85vh] flex items-center px-6 sm:px-10">
        <div  id="login" className="absolute inset-0 px-6 md:px-10 py-2 md:py-0">
          <div
            className="w-full h-full bg-cover bg-center rounded-xl"
            style={{ backgroundImage: `url(${herosection})` }}
          ></div>
        </div>

        <div className="relative z-10 flex flex-col md:flex-row justify-between w-full">
          <div className="w-full md:w-2/3 px-4 sm:px-20 mt-28 sm:mt-36 md:mt-20 lg:mt-44 text-center sm:text-left">
            <h1 className="text-3xl sm:text-5xl font-semibold leading-tight">
              Login: Access Orcas Valley <br /> Startup Platform
            </h1>
            <p className="mt-4 text-base sm:text-lg font-light max-w-lg mx-auto sm:mx-0">
              Access The Orca Blueprint, AI pitch deck generator, investor portal, 
              and funding opportunities. Ready to start your startup platform login?
            </p>

            <p  className="mt-4 text-base sm:text-lg font-light max-w-lg mx-auto sm:mx-0">Join startup accelerator access for $299. One-time payment</p>
          </div>

         <div  className="w-full md:w-[60%] h-[40vh] md:h-[50vh] mt-12 md:mt-20 flex justify-center items-center px-4 sm:px-8 z-10">
  <div className="p-6 bg-white/10 border border-white/20 backdrop-blur-sm shadow-xl text-white rounded-xl flex flex-col items-center justify-start w-full h-full max-w-xs sm:max-w-sm lg:gap-4">
   <img src="/favicon2.png" alt="My Icon"className="w-8 h-8 inline-block ml-2 align-baseline"/>
   
     {/* <h2 className="text-md sm:text-xl font-semibold mb-2 text-center w-full">Join now for $299. One-time payment.</h2> */}
     <h3 className="text-md sm:text-xl font-semibold mb-2 text-center w-full">Login or signup using your Google or Microsoft accounts.</h3>
    {/* <h2 className="text-xl sm:text-2xl font-semibold mb-4">Login/Register</h2> */}

    <div className="flex flex-col items-center justify-center gap-4 w-full">
      <div className="flex items-center justify-center w-[100%] transform scale-105">
        <GoogleOAuthProvider clientId="318353003910-7kvgkvmaallcedcjpb3fdslflojkhhj2.apps.googleusercontent.com">
         <div className="flex items-center justify-center w-full ">
        
        <div className="w-[80%] h-[56px] flex items-center justify-center   rounded-md transition lg:scale-110">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => console.error("Google login failed")}
            useOneTap
            width="100%" 
            theme="outline"
            text="signin_with"
            shape="rectangular"
            size="large"
          />
        </div>
      </div>
 
          
        </GoogleOAuthProvider>
      </div>

      <img
        src={MicrosoftIcon}
        onClick={handleMicrosoftLogin}
        alt="Microsoft Login"
        className="w-[65%] md:w-[73%] lg:w-[65%] h-[56px] mt-4 ml-2 hover:cursor-pointer hover:scale-105 transition-transform duration-200 rounded-md object-contain"
      />
    </div>
  </div>
</div>

        </div>

        {/* Vertical Side Text */}
        <div className="absolute top-6 left-2 xs:left-2 sm:top-10 sm:left-6 md:left-10 z-20 px-5 flex flex-col items-center text-gray-300 text-lg tracking-widest sm:flex">
          <div className="border-l-2 border-gray-300 h-8 sm:h-10 mb-2"></div>
          <p className="flex flex-col text-center text-xs">
            <span>T</span>
            <span>H</span>
            <span>E</span>
            <br />
            <span>O</span>
            <span>R</span>
            <span>C</span>
            <span>A</span>
            <span>S</span>
            <br />
            <span>W</span>
            <span>A</span>
            <span>Y</span>
          </p>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 mb-20">
  {/* Heading */}
  <div className="mb-8">
    <h3 className="text-3xl sm:text-4xl font-light text-gray-800">
      What's included?
    </h3>
  </div>

  {/* Content */}
  <div className="flex flex-col md:flex-row items-center gap-10">
    <div className="w-full md:w-1/2 flex flex-col items-start justify-center">
      <ul className="space-y-2 lg:space-y-6">
        {features.map((item, idx) => (
          <li
            key={idx}
            className="flex items-start gap-2 border-b pb-1 border-gray-300 last:border-none"
          >
            <FaCheck className="text-green-600 mt-1" />
            <span className="text-gray-800 text-sm sm:text-base md:text-lg">
              {item}
            </span>
          </li>
        ))}
      </ul>
    </div>

    {/* <div className="w-full md:w-1/2 flex items-center justify-center p-2">
      <div className="w-full min-h-[200px] flex items-center justify-center ">
        <ImageSlider slides={slides} bgColor="bg-gray-300" />
      </div>
    </div>
  </div> */}
          <div className="w-full md:w-1/2 flex justify-center lg:justify-center">
              <div className="w-full lg:w-[90%] h-[55vh] xl:h-[60vh] flex">
                <div
                  className="flex-1 h-[70%] w-[50%] bg-white flex items-center justify-center text-gray-700 text-lg font-medium rounded-md shadow-inner"
                  style={{
                    backgroundImage: `url(${ebook1})`,
                    backgroundSize: "contain",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center right",
                  }}
                ></div>
  
                <div
                  className="flex-1 mt-28 h-[70%] w-[50%] -ml-1 flex items-center justify-center text-gray-700 text-lg font-medium rounded-md shadow-inner"
                  style={{
                    backgroundImage: `url(${ebook2})`,
                    backgroundSize: "contain",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center left",
                  }}
                >

                </div>
              </div>
            </div>
            </div>
</section>

      {/* <div className="flex items-center justify-center  w-full  p-2 mb-4">
        <div
          className="h-full bg-white rounded-md shadow-inner w-[90%] md:w-[85%] min-h-[12rem] md:min-h-[34rem] overflow-hidden bg-no-repeat bg-contain  bg-center"
          style={{ backgroundImage: `url("${image3}")` }}
        />
        
      </div> */}


      <div className="w-full  flex items-center justify-center p-2 mb-8">
      <div className="w-full min-h-[200px] flex items-center justify-center">
         <div
      className={`rounded-lg shadow-lg   bg-white text-white space-y-6 flex flex-col w-full h-full`}
    >
      <div className="relative flex items-center justify-center aspect-auto rounded-lg overflow-hidden w-full h-full max-h-[100vh]  px-4 lg:px-10">
        {/* <img
          src={image3}
          alt="Slide"
          className="w-[90%] h-full object-cover"
        /> */}
          <ImageSlider slides={slides} bgColor="bg-gray-300" />

      </div>
    </div>
      </div>
    </div>
        
      
      <section className="w-full bg-gradient-to-r from-teal-100 via-white to-white py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <h2 className="text-3xl sm:text-4xl font-medium text-gray-800 text-center md:text-left">
            Ready to <span className="text-[#02C4A1]">get started?</span>
          </h2>

          <div className="flex flex-col items-center md:items-end text-center md:text-center space-y-2 w-full md:w-auto">
            <div className="text-gray-700 text-xl leading-tight">
              <p>Join now. Launch with</p>
              <p>real tools, no delays.</p>
            </div>
            <button className="flex items-center justify-center gap-2 bg-[#02C4A1] hover:bg-[#072928] text-white px-6 py-2 rounded-full font-medium transition"
               >
                <a href="#login" className="flex items-center justify-center gap-2">Join Now <FaArrowRight /></a>
              
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Login;
